import React, {  } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@mui/material";
import HomeCarousel from "../customer/Components/Carousel/HomeCarousel";
import HomeProductSection from "../customer/Components/Home/HomeProductSection";
import HomeCuratedSection from "../customer/Components/Home/HomeCuratedSection";
import VideoFeed from "../customer/Components/VideoFeed/VideoFeed";
import MarketingClaims from "../customer/Components/MarketingClaims/MarketingClaims";
import useSiteSettings from "../hooks/useSiteSettings";

const CollectionTile = ({ c }) => {
  const navigate = useNavigate();

  return (
    <div
      onClick={() => navigate(c.link || c.to)}
      className="group relative h-52 cursor-pointer overflow-hidden rounded-2xl bg-[#5e3e3e] shadow-lg sm:h-72"
    >
      {/* Uploaded tile image only — no fallback. */}
      {c.image && (
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${c.image})` }}
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10" />
      {/* Half-width tiles on phones: smaller title, description capped at two
          lines, tighter padding — so the text never crowds the image. */}
      <div className="relative z-10 flex h-full flex-col justify-end p-3.5 text-[#f1e7da] sm:p-6">
        <h3 className="font-display text-xl font-bold leading-tight drop-shadow sm:text-3xl sm:leading-none">{c.title || c.name}</h3>
        <p className="mt-1 line-clamp-2 text-xs leading-snug text-[#f1e7da]/85 sm:text-sm">{c.desc}</p>
        <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#e0b65c] transition-all duration-300 group-hover:gap-2 sm:mt-3 sm:text-sm">
          Explore the range →
        </span>
      </div>
    </div>
  );
};

const SectionHeading = ({ eyebrow, title }) => {
  if (!eyebrow && !title) return null;
  return (
    <div className="mb-10 text-center">
      {eyebrow && (
        <p className="mb-2 text-xs font-semibold uppercase tracking-[0.3em] text-[#b28585]">
          {eyebrow}
        </p>
      )}
      {title && (
        <h2 className="font-display text-4xl font-bold text-[#511d00] sm:text-5xl">
          {title}
        </h2>
      )}
      <div className="mx-auto mt-3 h-[3px] w-20 rounded-full bg-gradient-to-r from-[#b28585] to-[#b2a885]" />
    </div>
  );
};

const Homepage = () => {
  const navigate = useNavigate();
  const s = useSiteSettings();
  const supportPhone = s.contactPhone || "";
  // No hardcoded fallbacks — sections come purely from admin Settings, and hide
  // themselves when the admin hasn't configured any.
  const tiles = s.collectionTiles || [];
  const featured = s.featuredSections || [];

  return (
    <div className="bg-[#fffbf2]">
      {/* Hero */}
      <HomeCarousel />

      {/* Shop by Collection */}
      {tiles.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 py-16">
          <SectionHeading
            eyebrow={s.homeCollectionsEyebrow}
            title={s.homeCollectionsTitle}
          />
          {/* Two per row on phones (tile text is sized down to fit, below).
              Wider screens: columns fitted to the tile count so none sits
              alone on a row. */}
          <div
            className={`grid grid-cols-2 gap-3 sm:gap-5 ${
              tiles.length === 4 ? "lg:grid-cols-4" : tiles.length % 3 === 0 ? "md:grid-cols-3" : ""
            }`}
          >
            {tiles.map((c, i) => (
              <CollectionTile key={c.title || c.name || i} c={c} index={i} />
            ))}
          </div>
        </section>
      )}

      {/* New Arrivals */}
      {s.showNewArrivals !== false && (
        <HomeCuratedSection
          className="pb-4 pt-2"
          heading={s.newArrivalsHeading}
          eyebrow="Just Arrived"
          endpoint="/api/products/new-arrivals?limit=12"
        />
      )}

      {/* Featured product carousels */}
      {featured.length > 0 && (
        <section className="space-y-12 pb-16 pt-12">
          {featured.map((f, i) => (
            <HomeProductSection
              key={`${f.query}-${i}`}
              section={f.title}
              query={f.query}
              eyebrow={f.eyebrow}
              link={f.link}
              view_all
            />
          ))}
        </section>
      )}

      {/* Best Sellers */}
      {s.showBestSellers !== false && (
        <HomeCuratedSection
          className="pb-16"
          heading={s.bestSellersHeading}
          eyebrow="Most Loved"
          endpoint="/api/products/best-sellers?limit=12"
        />
      )}

      {/* Heritage story */}
      {(s.homeHeritageHeading || s.homeHeritageText) && (
        <section className="bg-[#F8ECD7] py-20">
          <div className="mx-auto max-w-4xl px-6 text-center">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.3em] text-[#b28585]">
              Our Heritage
            </p>
            {s.homeHeritageHeading && (
              <h2 className="mb-5 font-display text-4xl font-bold text-[#511d00] sm:text-5xl">
                {s.homeHeritageHeading}
              </h2>
            )}
            {s.homeHeritageText && (
              <p className="mx-auto max-w-3xl text-base leading-relaxed text-[#5e3e3e] sm:text-lg">
                {s.homeHeritageText}
              </p>
            )}
            <Button
              onClick={() => navigate("/about")}
              variant="contained"
              sx={{ mt: 4, borderRadius: "999px", px: 4, py: 1 }}
            >
              Discover Our Story
            </Button>
          </div>
        </section>
      )}

      {/* Video reels */}
      {(s.reels || []).length > 0 && (
        <div className="py-4">
          <VideoFeed section={s.reelsHeading} />
        </div>
      )}

      {/* Custom & bulk orders CTA */}
      {(s.homeBulkHeading || s.homeBulkText) && (
      <section className="px-4 py-16">
        <div className="mx-auto max-w-6xl overflow-hidden rounded-3xl bg-gradient-to-r from-[#5e3e3e] to-[#511d00] px-6 py-12 text-center text-[#f1e7da] shadow-xl sm:px-10">
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.3em] text-[#e0b65c]">
            Weddings · Baraat · Groom's Party
          </p>
          {s.homeBulkHeading && (
            <h2 className="mb-3 font-display text-3xl font-bold sm:text-4xl">
              {s.homeBulkHeading}
            </h2>
          )}
          {s.homeBulkText && (
            <p className="mx-auto mb-7 max-w-2xl text-[#f1e7da]/85">
              {s.homeBulkText}
            </p>
          )}
          {/* Always show the CTA — opens the Contact page. */}
          <Button
            onClick={() => navigate("/contact")}
            variant="contained"
            sx={{
              borderRadius: "999px",
              px: 4,
              py: 1,
              color: "#3d2417",
              fontWeight: 700,
              background: "linear-gradient(135deg,#f0ce82,#cba24b)",
              "&:hover": { background: "linear-gradient(135deg,#f4d98f,#cba24b)" },
            }}
          >
            Get in Touch{supportPhone ? ` · ${supportPhone}` : ""}
          </Button>
        </div>
      </section>
      )}

      <MarketingClaims />
    </div>
  );
};

export default Homepage;
